/**
 * Centralized Endpoints Registry
 * All API endpoints are defined here for single source of truth
 */

export const ENDPOINTS = {
  // Health and status
  health: '/health',
  
  // Expat questionnaire endpoints
  expat: {
    completeQuestionnaire: '/expat/questionnaire/complete',
    saveQuestionnaire: '/expat/questionnaire/save',
    startQuestionnaire: '/expat/questionnaire/start',
    answerQuestion: '/expat/questionnaire/answer',
  },
  
  // Questionnaire endpoints (new questionnaire system)
  questionnaire: {
    list: '/questionnaire',
    get: '/questionnaire/:id',
    create: '/questionnaire',
    update: '/questionnaire/:id',
    delete: '/questionnaire/:id',
    getQuestions: '/questionnaire/:id/questions',
    // Sessions
    createSession: '/questionnaire/session',
    getSession: '/questionnaire/session/:sessionId',
    updateSession: '/questionnaire/session/:sessionId',
    deleteSession: '/questionnaire/session/:sessionId',
    getSessionSummary: '/questionnaire/session/:sessionId/summary',
    getNextQuestion: '/questionnaire/session/:sessionId/next',
    submitAnswer: '/questionnaire/session/:sessionId/answer',
    completeSession: '/questionnaire/session/:sessionId/complete',
    getSessionAnswers: '/questionnaire/session/:sessionId/answers',
  },
  
  // Tax calculation endpoints
  tax: {
    calculate: '/tax/calculate',
    calculateDetailed: '/tax/calculate-detailed',
    calculateFromQuestionnaire: '/tax/calculate-from-questionnaire',
    quickEstimate: '/tax/quick-estimate',
  },
  
  // Tax rules endpoints
  rules: {
    list: '/rules/rules',
    get: '/rules/rules/:id',
    getByYear: '/rules/rules',
    getByCategory: '/rules/rules',
    summary: '/rules/rules/summary',
    categories: '/rules/rules/categories',
    years: '/rules/rules/years',
  },
  
  // Extraction endpoints
  extraction: {
    uploadFile: '/extract/upload-file',
    extractText: '/extract/extract-text',
    extractAndSave: '/extract/extract-and-save',
    downloadCsv: '/extract/download-csv',
  },
  
  // Validation endpoints
  validation: {
    validateRules: '/validation/rules',
    analyzePatterns: '/validation/analyze-patterns',
  },

  // Calculation records (audit .md files)
  calculationRecords: {
    list: '/calculation-records',
    get: '/calculation-records/:filename',
    download: '/calculation-records/:filename/download',
    delete: '/calculation-records/:filename',
  },

  /** Native browser TLS client-certificate login (like sede.aeat.es) */
  certAuth: {
    /**
     * Cert-login must hit port 8443 — the Nginx listener configured with
     * ssl_verify_client so the browser cert picker fires during the TLS handshake.
     * All other endpoints use the normal port-443 / same-origin baseURL path.
     *
     * Built at runtime from window.location so it works on any domain/environment
     * with no hardcoded hostname, IP, or domain.
     *
     * Examples:
     *   https://taxcalculator.disrupt.ltd      → https://taxcalculator.disrupt.ltd:8443/api/v1/auth/cert-login
     *   https://staging.example.com            → https://staging.example.com:8443/api/v1/auth/cert-login
     *   http://localhost:3000 (dev)            → http://localhost:8443/api/v1/auth/cert-login
     */
    get login(): string {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      // Replace whatever port (or no port) with :8443
      const base = origin.replace(/:\d+$/, '') + ':8443';
      return `${base}/api/v1/auth/cert-login`;
    },
  },

  /** App ↔ Calc integration — token exchange entry point */
  calcEntry: {
    init: '/calc-entry/init',
  },

  /** Admin review surface – gated by X-Admin-Token header on the backend */
  adminReview: {
    users: '/admin/review/users',
    user: '/admin/review/users/:userUuid',
    userByNif: '/admin/review/users/by-nif/:nif',
    flowSession: '/admin/review/flow-sessions/:sessionUuid',
    flowSessionAeat: '/admin/review/flow-sessions/:sessionUuid/aeat',
    aeatSnapshot: '/admin/review/aeat-snapshots/:snapshotUuid',
  },

  /** User-facing declaration document download */
  declarationDocuments: {
    list:     (sessionUuid: string) => `/settings/declarations/${sessionUuid}/documents`,
    download: (docId: string)       => `/settings/documents/${docId}/download`,
  },

  /** Admin document management – gated by X-Admin-Token */
  adminDocuments: {
    list:     (userId: string) => `/admin/review/users/${userId}/documents`,
    upload:   (userId: string) => `/admin/review/users/${userId}/documents`,
    download: (docId: string)  => `/admin/review/documents/${docId}/download`,
    delete:   (docId: string)  => `/admin/review/documents/${docId}`,
  },

  /** AEAT Renta fiscal-data BFF (server-side only; see docs/AEAT_FISCAL_FLOWS.md) */
  aeatFiscal: {
    createSession: '/aeat/fiscal/session',
    getSession: '/aeat/fiscal/session/:sessionId',
    certificateUpload: '/aeat/fiscal/session/:sessionId/certificate',
    ratification: '/aeat/fiscal/session/:sessionId/ratification',
    claveStart: '/aeat/fiscal/session/:sessionId/clave/start',
    clavePin: '/aeat/fiscal/session/:sessionId/clave/pin',
    claveResend: '/aeat/fiscal/session/:sessionId/clave/resend',
    reference: '/aeat/fiscal/session/:sessionId/reference',
    fiscalData: '/aeat/fiscal/session/:sessionId/fiscal-data',
  },
} as const;

/**
 * Path template replacement
 * Replaces :param with actual values
 * 
 * @example
 * path('/users/:id', { id: '123' }) => '/users/123'
 * path('/users/:userId/posts/:postId', { userId: '1', postId: '2' }) => '/users/1/posts/2'
 */
export function path(
  template: string,
  params?: Record<string, string | number>
): string {
  if (!params) return template;
  
  return template.replace(/:(\w+)/g, (match, key) => {
    const value = params[key];
    if (value === undefined || value === null) {
      throw new Error(`Missing required path parameter: ${key}`);
    }
    return String(value);
  });
}

/**
 * Append query parameters to URL
 * Handles arrays, null/undefined values, and encoding
 * 
 * @example
 * withQuery('/api/users', { page: 1, limit: 10 }) => '/api/users?page=1&limit=10'
 * withQuery('/api/users', { tags: ['a', 'b'] }) => '/api/users?tags=a&tags=b'
 * withQuery('/api/users', { search: null }) => '/api/users'
 */
export function withQuery(
  url: string,
  query?: Record<string, string | number | boolean | (string | number | boolean)[] | null | undefined>
): string {
  if (!query) return url;
  
  const params = new URLSearchParams();
  
  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return; // Skip null/undefined values
    }
    
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== null && item !== undefined) {
          params.append(key, String(item));
        }
      });
    } else {
      params.append(key, String(value));
    }
  });
  
  const queryString = params.toString();
  if (!queryString) return url;
  
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${queryString}`;
}

/**
 * Helper to build full endpoint URL
 * Combines path replacement and query parameters
 */
export function buildUrl(
  template: string,
  options?: {
    params?: Record<string, string | number>;
    query?: Record<string, string | number | boolean | (string | number | boolean)[] | null | undefined>;
  }
): string {
  let url = path(template, options?.params);
  url = withQuery(url, options?.query);
  return url;
}


